

import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Button, TextField, Grid, Paper, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter,
    useTheme, Divider, FormControl, Autocomplete, Stack
} from "@mui/material";
import CustomTableContainer from "../../../components/table/CustomTableContainer";
import { tokens } from "../../../theme/theme";
import Header from "../../../Layout/Header";
import useShiftCode from "../../../hooks/useShiftCode";

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";
import 'dayjs/plugin/customParseFormat';
import useEmployee from "../../../hooks/useEmployee";
import useGetExpenseCategories from "../../../hooks/useExpensesCategoriesByItemId";


const PowerConsumptionModule = ({ handleBackWinderMachineEntry }) => {

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [powerData, setPowerData] = useState([]);

    // load shift code
    const { shiftCode, selectedShiftCode, selectedShiftCodeID, setSelectedShiftCode, setSelectedShiftCodeID, loadShiftCodeByID, } = useShiftCode(0);
    const { employee, selectedEmployee, selectedEmployeeID, setSelectedEmployee, setSelectedEmployeeID, loadEmployeeByID } = useEmployee(0);
    const { expenseCategories, setExpensesCategories, } = useGetExpenseCategories(3);

    const [formData, setFormData] = useState({
        shift_id: 0,
        employee_id: selectedEmployeeID,
        shift_date: new Date().toISOString().split('T')[0], // 👈 current date in YYYY-MM-DD
        company_id: localStorage.getItem('companyid'),
        plant_id: 1,
        category_id: 3,
        user: localStorage.getItem('userID'),
        expenseItems: [], // key: expense ID, value: { unit_price, consumed }
    });
    
    const [editMode, setEditMode] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [shiftDate, setShiftDate] = useState(dayjs());





    const handleChangeShiftCode = (event, value) => {
        setSelectedShiftCode(value);
        loadShiftCodeByID(value.shiftid);

        setFormData((prev) => ({
            ...prev,
            shift_id: value.shiftid,
        }));
    }

    // Fetch existing power consumption data
    useEffect(() => {
        axios
            .get("http://localhost:5000/power-consumption")
            .then((response) => setPowerData(response.data))
            .catch((error) => console.error("Error fetching data", error));
    }, []);

    useEffect(() => {
        if (expenseCategories.length > 0) {
            const items = expenseCategories.map((item) => ({
                id: item.id,
                unit_price: item.uom ?? "",       // uom is being treated as unit_price
                consumed: item.consumed ?? "",
                total_cost: item.total_cost ?? 0,
            }));

            setFormData((prev) => ({
                ...prev,
                expenseItems: items,
            }));
        }
    }, [expenseCategories]);


    const handleChange = (e, id, field) => {
        const value = e.target.value;

        setFormData((prev) => {
            const updatedItems = prev.expenseItems.map((item) => {
                if (item.id === id) {
                    const updatedItem = {
                        ...item,
                        [field]: value,
                    };

                    const unit_price = parseFloat(updatedItem.unit_price) || 0;
                    const consumed = parseFloat(updatedItem.consumed) || 0;
                    updatedItem.total_cost = unit_price * consumed;

                    return updatedItem;
                }
                return item;
            });

            return {
                ...prev,
                expenseItems: updatedItems,
            };
        });
    };



    // Handle form submission (add or update data)
    const handleSubmit = (e) => {
        e.preventDefault();

        const apiUrl = "http://localhost:5000/power-consumption";

        const resetForm = () => {
            setFormData({
                shift_id: 0,
                employee_id: selectedEmployeeID,
                shift_date: new Date().toISOString().split('T')[0], // 👈 current date in YYYY-MM-DD
                company_id: localStorage.getItem('companyid'),
                plant_id: 1,
                category_id: 3,
                user: localStorage.getItem('userID'),
                expenseItems: [], // key: expense ID, value: { unit_price, consumed }
            });
            setEditMode(false);
            setCurrentId(null);
        };

        if (editMode) {
            axios
                .put(`${apiUrl}/${currentId}`, formData)
                .then((response) => {
                    const updatedData = powerData.map((item) =>
                        item.id === response.data.id ? response.data : item
                    );
                    setPowerData(updatedData);
                    resetForm();
                })
                .catch((error) => {
                    console.error("Error updating data", error);
                });
        } else {
            axios
                .post(apiUrl, formData)
                .then((response) => {
                    setPowerData([...powerData, response.data]);
                    resetForm();
                })
                .catch((error) => {
                    console.error("Error adding data", error);
                });
        }
    };


    // Calculate total cost (unit price * quantity)
    const calculateTotalCost = (unitPrice, quantity) => {
        return unitPrice && quantity ? unitPrice * quantity : 0;
    };

    // Handle Delete
    const handleDelete = (id) => {
        axios
            .delete(`http://localhost:5000/power-consumption/${id}`)
            .then(() => {
                setPowerData(powerData.filter((item) => item.id !== id));
            })
            .catch((error) => console.error("Error deleting data", error));
    };

    // Handle Edit
    const handleEdit = (data) => {
        setFormData(data);
        setEditMode(true);
        setCurrentId(data.id);
    };

    return (
        <Box >

            <Header title={" Power Consumption "} subtitle="" callback={handleBackWinderMachineEntry} />
            <Divider />
            <Box sx={{ padding: 1 }}>

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2} padding={1} >
                        <Grid item xs={12} sm={12} lg={12} xl={6}>
                            <Grid container padding="5px 0px 5px 5px" spacing={{ xs: 2, sm: 2, md: 2, lg: 1, xl: 1 }} alignItems="center">
                                <Grid item xs={3} sm={3} md={3} lg={1.5} xl={2}>
                                    <Typography variant="h6" gutterBottom>
                                        Shift Code / Date
                                    </Typography>
                                </Grid>
                                <Grid item xs={2} sm={2} md={1} lg={1} xl={1.2}>
                                    <FormControl fullWidth size="small">
                                        <Autocomplete
                                            fullWidth
                                            value={selectedShiftCode}
                                            onChange={handleChangeShiftCode}
                                            size="small"
                                            style={{ fontSize: '12px' }}
                                            options={shiftCode}
                                            getOptionLabel={(option) => `${option.shiftcode || ''}`}
                                            renderInput={(params) =>
                                                <TextField required
                                                    {...params} variant="outlined" />}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={2} sm={2} md={1} lg={1.7} xl={2.5}>
                                    <FormControl fullWidth size="small">
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <Stack spacing={1}>
                                                <DatePicker
                                                    value={dayjs(shiftDate)}
                                                    onChange={(date) => {
                                                        setShiftDate(date);
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            shift_date: dayjs(date).format("YYYY-MM-DD"),

                                                        }));
                                                    }}
                                                    slotProps={{ textField: { size: 'small', variant: "outlined" } }}
                                                    fullWidth
                                                    format="DD-MMM-YYYY" // Use the appropriate format string
                                                    renderInput={(props) => (
                                                        <TextField
                                                            {...props}
                                                        />
                                                    )}
                                                />
                                            </Stack>
                                        </LocalizationProvider>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={1} sm={1} md={1} lg={1.5} xl={2}>
                                    <Typography variant="h6" gutterBottom>
                                        Employee Code
                                    </Typography>
                                </Grid>

                                <Grid item xs={2} sm={2} md={1} lg={2.6} xl={3.5}>
                                    <FormControl fullWidth size="small">
                                        <Autocomplete
                                            size="small"
                                            fullWidth
                                            options={employee}
                                            value={selectedEmployee}
                                            onChange={(event, newValue) => {
                                                setSelectedEmployee(newValue);
                                                setSelectedEmployeeID(newValue.id);
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    employee_id: newValue?.id || 0,
                                                }));
                                            }}
                                            getOptionLabel={(option) => option.name || ''}
                                            renderInput={(params) => (
                                                <TextField {...params} label="" required variant="outlined" />
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Divider sx={{ marginTop: "10px" }} />

                    <Box padding={1} sx={{ borderRadius: "5px" }}>
                        <Grid container>
                            <Grid item lg={8} xl={6}>
                                <Typography variant="h4" gutterBottom sx={{ marginTop: 1, fontWeight: "bold" }}>
                                    Power Consumption List
                                </Typography>

                                {/* Power Consumption Table */}
                                <CustomTableContainer colors={colors}>
                                    <TableContainer >
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell >Power Type</TableCell>
                                                    <TableCell>Unit Price (₹)</TableCell>
                                                    <TableCell>Consumed</TableCell>
                                                    <TableCell>Total Cost (₹)</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {/* {expenseCategories.map((item) => {
                                                    const key = item.code.toLowerCase().replace(/\s+/g, "_"); // e.g., "Solar Energy Usage" → "solar_energy_usage"
                                                    return (
                                                        <TableRow key={item.id}>
                                                            <TableCell>
                                                                {item.code}
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                            border: 'none',
                                                                        },
                                                                        '& .MuiInputBase-input': {
                                                                            padding: '8px',
                                                                            textAlign: 'right',
                                                                        },
                                                                    }}
                                                                    type="number"
                                                                    name={`${key}_unit_price`}
                                                                    value={formData[`${key}_unit_price`] || ""}
                                                                    onChange={(e) => handleChange(e, `${key}_unit_price`)}
                                                                    fullWidth
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                            border: 'none',
                                                                        },
                                                                        '& .MuiInputBase-input': {
                                                                            padding: '8px',
                                                                            textAlign: 'right',
                                                                        },
                                                                    }}
                                                                    type="number"
                                                                    name={`${key}_consumed`}
                                                                    value={formData[`${key}_consumed`] || ""}
                                                                    onChange={(e) => handleChange(e, `${key}_consumed`)}
                                                                    fullWidth
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                ₹
                                                                {calculateTotalCost(
                                                                    formData[`${key}_unit_price`] || 0,
                                                                    formData[`${key}_consumed`] || 0
                                                                ).toFixed(2)}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })} */}

                                                {expenseCategories.map((category) => {
                                                    const item = formData.expenseItems.find((i) => i.id === category.id) || {};

                                                    return (
                                                        <TableRow key={category.id}>
                                                            <TableCell>{category.code}</TableCell>

                                                            <TableCell>
                                                                <TextField
                                                                    type="number"
                                                                    value={item.unit_price || ""}
                                                                    onChange={(e) => handleChange(e, category.id, "unit_price")}
                                                                    fullWidth
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                                                        '& .MuiInputBase-input': {
                                                                            padding: '8px',
                                                                            textAlign: 'right',
                                                                        },
                                                                    }}
                                                                />
                                                            </TableCell>

                                                            <TableCell>
                                                                <TextField
                                                                    type="number"
                                                                    value={item.consumed || ""}
                                                                    onChange={(e) => handleChange(e, category.id, "consumed")}
                                                                    fullWidth
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                                                        '& .MuiInputBase-input': {
                                                                            padding: '8px',
                                                                            textAlign: 'right',
                                                                        },
                                                                    }}
                                                                />
                                                            </TableCell>

                                                            <TableCell>
                                                                ₹{(item.total_cost || 0).toFixed(2)}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}

                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CustomTableContainer>
                                <Grid item xs={12} sx={{ marginTop: 2, display: "flex", justifyContent: "flex-end" }}>
                                    <Button type="submit" variant="contained" color="secondary">
                                        {editMode ? "Update" : "Add"} Power Consumption
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>

                    </Box>


                </form>

                {/* <Typography variant="h6" gutterBottom sx={{ marginTop: 3 }}>
                    Existing Power Consumption Records
                </Typography>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Shift ID</TableCell>
                                <TableCell>Electricity Total Cost (₹)</TableCell>
                                <TableCell>Fuel Total Cost (₹)</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {powerData.map((data) => (
                                <TableRow key={data.id}>
                                    <TableCell>{data.shift_id}</TableCell>
                                    <TableCell>
                                        ₹{calculateTotalCost(data.electricity_unit_price, data.electricity_consumed).toFixed(2)}
                                    </TableCell>
                                    <TableCell>
                                        ₹{calculateTotalCost(data.fuel_unit_price, data.fuel_consumed).toFixed(2)}
                                    </TableCell>
                                    <TableCell>
                                        <Button onClick={() => handleEdit(data)} variant="contained" color="primary" size="small" style={{ marginRight: 5 }}>
                                            Edit
                                        </Button>
                                        <Button onClick={() => handleDelete(data.id)} variant="contained" color="secondary" size="small">
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer> */}
            </Box>
        </Box>
    );
};

export default PowerConsumptionModule;


